import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';

import type { Filters } from '@/types/filters.ts';
import type { Product, ProductDTO } from '@/types/products';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { formatPrice } from '@/utils/utils';

export const useProductsStore = defineStore('products', () => {
  /* VARIABLES */
  const products = ref<Array<Product>>([]);
  const totalResults = ref(0);
  const nextPaginationKey = ref('');

  /* STORE IMPORTS */
  const metricsStore = useMetricsStore();
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { currentLocale, globalConfiguration, apiURL } = storeToRefs(globalConfigurationStore);

  /* METHODS */
  async function fetchProductsList(filters: Filters, signal?: AbortSignal) {
    const payloadFilters = JSON.parse(JSON.stringify(filters));
    try {
      const productDTOs = await getProductsList(payloadFilters, signal);
      products.value = formatToProducts(productDTOs.data);
      totalResults.value = productDTOs.total;
      nextPaginationKey.value = productDTOs.nextPage;
      await metricsStore.computeMetrics(productDTOs);
    }
    catch (err) {
      console.error('Unable to fetch data', err);
    }
  }

  async function fetchPaginatedProductsList(filters: Filters, signal?: AbortSignal) {
    if (!globalConfiguration.value || !globalConfiguration.value.apikey) {
      throw new Error('No client token available');
    }
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${globalConfiguration.value.apikey}`);
    const payloadFilters = JSON.parse(JSON.stringify(filters));
    payloadFilters.cursor = nextPaginationKey.value;
    const body = JSON.stringify(payloadFilters);
    try {
      const request = await fetch(`${apiURL.value}/products/list`, { method: 'POST', body, headers, signal });
      const productDTOs = await request.json();
      products.value.push(...formatToProducts(productDTOs.data));
      nextPaginationKey.value = productDTOs.nextPage;
    }
    catch (err) {
      console.error('Unable to fetch paginated data', err);
    }
  }

  async function getProductsList(filters: Filters, signal?: AbortSignal) {
    if (globalConfiguration.value?.apikey == null) {
      throw new Error('No client token available');
    }
    const path = '/products/list';
    const payloadFilters = JSON.parse(JSON.stringify(filters));
    const customFilterImported = payloadFilters.filters.find((it: any) => it.filterProperties.type === 'custom_filter' && it.filterProperties.target === 'product' && it.filterProperties.field === 'imported_in_langs');
    if (!customFilterImported) {
      payloadFilters.filters.push({
        filterProperties: {
          type: 'custom_filter',
          target: 'product',
          field: 'imported_in_langs',
        },
        selection: currentLocale.value,
      });
    }
    const body = JSON.stringify(payloadFilters);
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    headers.set('Authorization', `Bearer ${globalConfiguration.value?.apikey}`);
    const request = await fetch(`${apiURL.value}${path}`, { method: 'POST', body, headers, signal });
    return await request.json();
  }

  function formatToProducts(activities: Array<ProductDTO>) {
    const productsList: Array<Product> = [];
    activities?.forEach((activity) => {
      const {
        id,
        bookingUrls,
        currency,
        images,
        meetingPoint,
        names,
        priceFrom,
        originalPrice,
        source,
        sourceId,
        properties,
        importedInLangs,
      } = activity;
      const name = names.find(it => it.lang === currentLocale.value)?.value ?? activity.name;
      const bookingUrl = bookingUrls.find(it => it.lang === currentLocale.value)?.value ?? activity.bookingUrl;

      const marketingTags = properties
        .filter(it => ['isInDemand', 'isSoldOut', 'isRecentlyPublished', 'isAlmostFull'].includes(it.propertyName))
        .filter((it: any) => Boolean(it.value))
        .sort((a: any, b: any) => a.metadata.priority - b.metadata.priority);

      const product: Product = {
        id,
        name,
        bookingUrl,
        image: images?.[0]?.url ?? null,
        images,
        importedInLangs,
        location: meetingPoint?.location ?? null,
        meetingPoint: {
          description: meetingPoint.description ?? '',
          nearestPublicTransport: meetingPoint.nearestPublicTransport ?? '',
        },
        priceFrom,
        originalPrice,
        currency,
        properties,
        source,
        sourceId,
        displayedPrice: getDisplayedPrice(activity),
      };
      if (marketingTags.length && 'metadata' in marketingTags[0]) {
        product.marketingTag = marketingTags[0].metadata;
      }
      productsList.push(product);
    });
    return productsList;
  }

  function getDisplayedPrice(product: ProductDTO) {
    const priceFromSettings = product.properties.find(it => it.propertyName === 'productEnrichmentByDistributor')?.value.price_from_settings?.options?.[0];

    const unitId = priceFromSettings?.units?.[0]?.id;
    const matchingOption = product.options.find(it => it.id === priceFromSettings?.id);
    const unit = matchingOption?.units.find((it: any) => it.id === unitId);

    const dataSource = unit ?? {
      price: product.priceFrom,
      originalPrice: product.originalPrice,
    };

    const { price, originalPrice } = dataSource;
    const hasDiscount = Boolean(originalPrice && Math.round(originalPrice) > Math.round(price));

    return {
      hasDiscount,
      originalPrice: formatPrice(originalPrice ?? 0),
      priceFrom: formatPrice(price ?? 0),
    };
  }

  return {
    products,
    totalResults,
    nextPaginationKey,
    fetchProductsList,
    fetchPaginatedProductsList,
    getProductsList,
    getDisplayedPrice,
    formatToProducts,
  };
});
